/**
 * WebSocket Manager (Frontend)
 *
 * Singleton that maintains a persistent WebSocket connection to the
 * control-tower backend.  Components subscribe to typed events and
 * are notified whenever the server pushes a matching message.
 *
 * Usage:
 *   import wsManager from "../utils/websocketManager";
 *
 *   // Connect (idempotent)
 *   wsManager.connect(clientId);
 *
 *   // Subscribe
 *   const unsub = wsManager.on("kanbanDataRefresh", (payload) => { … });
 *
 *   // Cleanup
 *   unsub();
 */

import config from "../config";
import { getOrCreateDeviceId } from "./deviceId.util";

const WS_URL = config.wsURL;
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;

class WebSocketManager {
  constructor() {
    this._ws = null;
    this._clientId = null;
    this._listeners = {}; // { eventType: Set<callback> }
    this._reconnectTimer = null;
    this._reconnectAttempts = 0;
    this._intentionallyClosed = false;
  }

  /**
   * Connect to the WebSocket server and register the client.
   * Safe to call multiple times – reconnects only when needed.
   * @param {string} user_id  – the current user's _id
   */
  connect(user_id) {
    console.log("ws url", WS_URL);

    if (user_id) this._clientId = user_id;

    // Already open or connecting
    if (
      this._ws &&
      (this._ws.readyState === WebSocket.OPEN ||
        this._ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this._intentionallyClosed = false;
    console.log(`🔌 [WS] Connecting to ${WS_URL}`);

    try {
      this._ws = new WebSocket(WS_URL);
    } catch (err) {
      console.error("❌ [WS] Failed to create WebSocket:", err.message);
      this._scheduleReconnect();
      return;
    }

    this._ws.onopen = () => {
      console.log("✅ [WS] Connected");
      this._reconnectAttempts = 0;

      // Register this client so the server can push targeted messages
      if (this._clientId) {
        const deviceId = getOrCreateDeviceId();
        this._send({
          type: "registerUser",
          userId: this._clientId,
          deviceId: deviceId,
        });
        console.log(`📱 [WS] Registered with Device ID: ${deviceId}`);
      }
    };

    this._ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this._dispatch(message);
      } catch (err) {
        console.warn("⚠️  [WS] Failed to parse message:", err.message);
      }
    };

    this._ws.onerror = (err) => {
      console.error("❌ [WS] WebSocket error:", err);
    };

    this._ws.onclose = () => {
      console.warn("🔴 [WS] Connection closed");
      if (!this._intentionallyClosed) {
        this._scheduleReconnect();
      }
    };
  }

  /**
   * Disconnect and prevent automatic reconnection.
   */
  disconnect() {
    this._intentionallyClosed = true;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
    console.log("🔌 [WS] Disconnected");
  }

  /**
   * Subscribe to a specific message type.
   * @param {string}   eventType  – e.g. "kanbanDataRefresh"
   * @param {Function} callback   – called with the message payload
   * @returns {Function} unsubscribe function
   */
  on(eventType, callback) {
    if (!this._listeners[eventType]) {
      this._listeners[eventType] = new Set();
    }
    this._listeners[eventType].add(callback);

    // Return an unsubscribe function
    return () => {
      if (this._listeners[eventType]) {
        this._listeners[eventType].delete(callback);
      }
    };
  }

  // ── Private helpers ─────────────────────────────────────────────

  _send(payload) {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(payload));
    }
  }

  _dispatch(message) {
    const { type } = message;

    // Internal ping-pong handling
    if (type === "ping") {
      this._send({ type: "pong", timestamp: new Date().toISOString() });
      return;
    }

    // Forward to registered listeners
    const handlers = this._listeners[type];
    if (handlers && handlers.size > 0) {
      handlers.forEach((cb) => {
        try {
          cb(message);
        } catch (err) {
          console.error(`❌ [WS] Listener error for "${type}":`, err);
        }
      });
    }
  }

  _scheduleReconnect() {
    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(
        `❌ [WS] Reached max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}). Giving up.`,
      );
      return;
    }

    this._reconnectAttempts += 1;
    const delay = RECONNECT_DELAY_MS * Math.min(this._reconnectAttempts, 5);
    console.log(
      `🔄 [WS] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts})`,
    );

    this._reconnectTimer = setTimeout(() => {
      this.connect(this._clientId);
    }, delay);
  }
}

// Export as singleton
const wsManager = new WebSocketManager();
export default wsManager;
